import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./DeleteAccount.css";
import { PRIVACY_POLICY } from "../lib/landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^\S+@\S+\.\S+$/;

const DELETED_ITEMS = [
  "Profile info (name, email, phone, Safe(H)er ID)",
  "Trusted contacts list",
  "SOS event history",
  "Audio evidence recordings",
  "Live-location breadcrumbs shared with your Circle",
  "Pending & accepted Circle invitations",
  "App settings (voice trigger phrases, PIN hash, custom messages)",
];

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Delete your Safe(H)er account & data";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Permanently remove your Safe(H)er account and all associated data. GDPR Article 17 compliant."
    );
    setMeta("robots", "index, follow");
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/deletion-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          note: note.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setDone(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="da-page">
      <div className="da-container">
        <Link to="/" className="da-back" data-testid="da-back-home">
          ← Back to safeher.app
        </Link>

        <h1 className="da-h1" data-testid="da-title">
          Delete your Safe(H)er account
        </h1>
        <p className="da-subtitle">
          Permanently remove your account and every piece of data associated
          with it. This action cannot be undone.
        </p>

        <section className="da-section">
          <h2 className="da-h2">What will be deleted</h2>
          <ul className="da-list">
            {DELETED_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="da-section">
          <h2 className="da-h2">Timeline</h2>
          <p className="da-text">
            Requests submitted via this form are processed and completed{" "}
            <strong>within 30 days</strong>. For immediate deletion, log in to
            the Safe(H)er app and use <em>Settings → Delete my account</em>.
          </p>
        </section>

        <div className="da-callout" data-testid="da-fastest">
          <p className="da-callout-title">⚡ Fastest option — from within the app</p>
          <p className="da-callout-text">
            Open Safe(H)er, sign in, then go to{" "}
            <strong>Settings → Delete my account</strong>. After you re-enter
            your password, everything is erased instantly.
          </p>
        </div>

        {!done ? (
          <section className="da-section">
            <h2 className="da-h2">Request deletion by email</h2>
            <p className="da-text">
              Use this form if you have uninstalled the app or lost access to
              your account. Enter the email address you used to register.
            </p>

            <form className="da-form" onSubmit={handleSubmit} noValidate>
              <label className="da-label" htmlFor="da-email">
                Email address
              </label>
              <input
                id="da-email"
                type="email"
                className="da-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="da-email-input"
                autoComplete="email"
              />

              <label className="da-label" htmlFor="da-note">
                Note <span className="da-optional">(optional, max 500 characters)</span>
              </label>
              <textarea
                id="da-note"
                className="da-textarea"
                rows={4}
                maxLength={500}
                placeholder="Anything you'd like us to know…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="da-note-input"
              />

              {error && (
                <p className="da-error" data-testid="da-error" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="da-submit"
                disabled={submitting}
                data-testid="da-submit"
              >
                {submitting ? "Submitting…" : "Submit deletion request"}
              </button>
            </form>
          </section>
        ) : (
          <div className="da-success" data-testid="da-success">
            <p className="da-success-title">✅ Request received</p>
            <p className="da-success-text">
              We've queued your request. You will receive a confirmation email
              shortly. Actual deletion is completed <strong>within 30 days</strong>.
              Nothing else is required from you. If you didn't submit this
              request, you can safely ignore this — nothing is deleted until we
              process your request.
            </p>
          </div>
        )}

        <section className="da-section">
          <h2 className="da-h2">Contact us directly</h2>
          <p className="da-text">If you can't use this form, email us at:</p>
          <a
            className="da-contact-link"
            href="mailto:safherofficial@gmail.com?subject=Account%20deletion%20request"
            data-testid="da-contact-email"
          >
            safherofficial@gmail.com
          </a>
        </section>

        <footer className="da-footer">
          <p>Safe(H)er — your safety companion.</p>
          <p>
            GDPR Article 17 compliant · Google Play Data-Deletion policy
            compliant
          </p>
          <p>
            <a
              className="da-footer-link"
              href={PRIVACY_POLICY}
              target="_blank"
              rel="noreferrer"
              data-testid="da-privacy-link"
            >
              Privacy Policy
            </a>{" "}
            ·{" "}
            <a
              className="da-footer-link"
              href="mailto:safherofficial@gmail.com"
              data-testid="da-footer-email"
            >
              safherofficial@gmail.com
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
